import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { RootState } from '../redux/store';
import { createPatient, updatePatient, CreatePatientPayload, search_by_phone_birthdate, getUserFacingErrorMessage } from '../services/api';
import { setRequestMetadata, resolvePatient } from '../redux/slices/appointmentSlice';
import { StepCard } from '../components/StepCard';
import { Button } from '../components/Button';
import { PatientTypeEnum, PATIENT_TYPE_TO_BACKEND } from '../types';
import { NewPatientForm } from '../components/patient-details/NewPatientForm';
import { ExistingPatientForm } from '../components/patient-details/ExistingPatientForm';
import { isValidEmail, PHONE_REGEX, MIN_DOB, getYesterday } from '../utils/helpers';
import { SyncLoader } from 'react-spinners';
import { AlertCircle } from 'lucide-react';

const PATIENT_SAVE_FAILED_MESSAGE = 'Failed to save patient details. Please try again.';


export const StepPatientDetails = () => {
  const { patientType, patientDetails, request_id, existingPatientMatch } = useSelector((s: RootState) => s.appointment);
  const collectInsurance = useSelector((s: RootState) => s.auth.practice?.collect_insurance_details);
  const insuranceSetting = useSelector((s: RootState) => s.auth.practice?.insurance_setting);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  const proceed = () => {
    navigate(`../confirmation`);
  };

  // Validate insurance fields if insurance is enabled and user selected 'insurance' payment
  const isInsuranceValid = (): boolean => {
    // If insurance collection is disabled, it's always valid
    if (!collectInsurance) return true;

    // If no payment type selected yet, user must choose one
    if (!patientDetails.paymentType) return false;

    // If user selected 'myself', insurance is not required
    if (patientDetails.paymentType !== 'insurance') return true;

    // Provider and Policy Holder Name are always required
    const insDetails = patientDetails.insuranceDetails;
    if (!insDetails?.provider?.trim() || !insDetails?.policyHolderName?.trim()) return false;

    // Check conditional required fields based on backend settings
    const showDob = insuranceSetting?.display_policy_holder_dob ?? true;
    const requireDob = insuranceSetting?.require_policy_holder_dob ?? false;
    const showMemberId = insuranceSetting?.display_member_id ?? true;
    const requireMemberId = insuranceSetting?.require_member_id ?? false;

    if (showDob && requireDob && !insDetails?.policyHolderDob) return false;
    if (showMemberId && requireMemberId && !insDetails?.memberId?.trim()) return false;

    return true;
  };

  const canContinueNew = Boolean(
    patientDetails.firstName &&
    patientDetails.lastName &&
    PHONE_REGEX.test(patientDetails.phone) &&
    patientDetails.email &&
    isValidEmail(patientDetails.email) &&
    patientDetails.birth_date &&
    patientDetails.birth_date <= getYesterday() &&
    patientDetails.birth_date >= MIN_DOB &&
    isInsuranceValid()
  );

  const canContinueExisting = Boolean(existingPatientMatch);

  const isExistingPatient = patientType === PatientTypeEnum.Existing;
  const canContinue = isExistingPatient ? canContinueExisting : canContinueNew;

  const savePatient = async () => {
    if (!canContinue || saving) return;

    setSaving(true);
    setSaveError(null);
    try {
      const backendPatientType =
        patientType && PATIENT_TYPE_TO_BACKEND[patientType as PatientTypeEnum]
          ? PATIENT_TYPE_TO_BACKEND[patientType as PatientTypeEnum]
          : PATIENT_TYPE_TO_BACKEND[PatientTypeEnum.New];

      const resolved = resolvePatient(patientDetails, existingPatientMatch);

      const payload: CreatePatientPayload = {
        first_name: resolved.firstName,
        last_name: resolved.lastName,
        email: resolved.email,
        phone_number: resolved.phone,
        birth_date: resolved.birth_date,
        patient_type: backendPatientType,
      };

      if (resolved.id) {
        payload.patient_id = resolved.id;
      }
      if (patientDetails.external_resource_id) {
        payload.external_resource_id = patientDetails.external_resource_id;
      }
      if (patientDetails.notes) {
        payload.notes = patientDetails.notes;
      }

      if (patientDetails.paymentType === 'insurance' && patientDetails.insuranceDetails) {
        const ins = patientDetails.insuranceDetails;
        payload.insurance_carrier_name = ins.provider || '';
        payload.insurance_member_id = ins.memberId || '';
        payload.policy_holder_name = ins.policyHolderName || '';
        payload.policy_holder_dob = ins.policyHolderDob || '';
      } else {
        payload.insurance_carrier_name = '';
        payload.insurance_member_id = '';
        payload.policy_holder_name = '';
        payload.policy_holder_dob = '';
      }

      let savePatientRequest: () => Promise<any>;

      // Always check for existing patient/request first
      try {
        const searchRes = await search_by_phone_birthdate({
          first_name: payload.first_name,
          last_name: payload.last_name,
          email: payload.email,
          phone_number: payload.phone_number,
          birth_date: payload.birth_date || ''
        });

        if (searchRes.data.type === 'patient_request') {
          let requestId = searchRes.data.attributes.request_id || ''
          dispatch(setRequestMetadata({
            request_id: requestId,
            requestable_id: searchRes.data.id,
            requestable_type: 'PatientRequest'
          }));
          savePatientRequest = () => updatePatient(requestId, payload);
        } else {
          payload.patient_id = searchRes.data.id;
          savePatientRequest = () => createPatient(payload);
        }
      } catch (e: any) {
        if (request_id) {
          payload.patient_id = null;
          savePatientRequest = () => updatePatient(request_id, payload);
        }
        else {
          savePatientRequest = () => createPatient(payload);
        }
      }

      const response = await savePatientRequest();
      const data = response.data;
      const attributes = data.attributes;

      dispatch(setRequestMetadata({
        request_id: data.id,
        requestable_id: attributes.requestable_id,
        requestable_type: attributes.requestable_type,
      }));

      proceed();
    } catch (error: any) {
      console.error('Error saving patient details:', error);
      setSaveError(getUserFacingErrorMessage(error, PATIENT_SAVE_FAILED_MESSAGE));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="grid grid-cols-1 md:grid-cols-1">
        <aside className="hidden md:block" />
        <section className="md:col-span-2 space-y-4">
          <div className='flex flex-col items-center text-center md:items-start md:text-start'>
            <h1 className="text-2xl font-semibold text-black">Patient details</h1>
            <p className="text-sm text-gray-500">Provide your contact information for appointment confirmation.</p>
          </div>
          <StepCard className='overflow-y-auto'>
            {isExistingPatient ? <ExistingPatientForm /> : <NewPatientForm />}
          </StepCard>
          {saveError && (
            <div className="flex items-center justify-center md:justify-start gap-2 rounded-lg border border-red-200 bg-red-50 px-2 py-2">
              <AlertCircle size={16} className="shrink-0 text-red-600" />
              <p className="text-sm text-red-700">{saveError}</p>
            </div>
          )}
          <div className="flex justify-between">
            <Button title='Back' variant='secondary' onClick={() => navigate('../step-3')} />
            <Button title={saving ? <SyncLoader size={8} color="#fff" /> : 'Continue'} disabled={!canContinue || saving} onClick={savePatient} />
          </div>
        </section>
      </div>
    </Layout>
  );
};